/**
 * klubb-product — PDP-only behavior.
 *
 * - Ryggprint: toggle inputs visibility, prune empty/unchecked properties
 *   from form submission so blank line item properties don't reach orders.
 * - Thumbnail click → swaps main image source.
 * - Sticky bottom add-to-cart bar fades in when main CTA leaves viewport.
 *
 * Exports named functions for unit tests; the IIFE auto-wires when
 * a [data-klubb-product] element is present in the page.
 */

/* ============================================================
 * Personalization (ryggprint) — driven by traede.* print flags.
 * Property keys written to the garment line: _print_name / _print_initials /
 * _print_front_number / _print_back_number (underscore-hidden, like
 * _klubb_club). One number input feeds both number keys, each only when the
 * garment has that flag. The Trykk charge lines (one per print type, generic
 * front/back number products) carry no values — Traede reads the print
 * values from the garment line only (Karoline, 07.10.2026).
 * ============================================================ */

// Allowed character sets. Norwegian letters incl. æ ø å.
const PRINT_PATTERNS = {
  name: /^[A-Za-zÆØÅæøå ]+$/,      // name stripe: letters + spaces
  initials: /^[A-Za-zÆØÅæøå]+$/,   // initials: letters only
  number: /^[0-9]+$/,              // back/front number: digits only
};

// Per-field max lengths. Confirmed by the Trykk product titles (D41):
// "Navnstripe (Max 16 Bokstaver)", "Initialer (Max 4 bokstaver)". Keep in sync
// with the maxlength attrs in klubb-product.liquid / klubb-player-package.liquid.
const PRINT_MAX = { name: 16, initials: 4, number: 2 };

export function validatePrintField(value, type) {
  const v = value == null ? '' : String(value);
  if (v === '') return true; // blank = no print, always valid
  const max = PRINT_MAX[type];
  if (max != null && v.length > max) return false;
  const pattern = PRINT_PATTERNS[type];
  return pattern ? pattern.test(v) : true;
}

export function formatPrintSummary(state) {
  return [state.name, state.initials, state.number]
    .map((s) => (s == null ? '' : String(s).trim()))
    .filter((s) => s.length > 0)
    .join(' · ');
}

export function printHasValue(state) {
  return formatPrintSummary(state).length > 0;
}

export function isPrintComplete(state, confirmed) {
  // No personalization → add-to-cart allowed (personalization is optional).
  if (!printHasValue(state)) return true;
  const valid =
    validatePrintField(state.name, 'name') &&
    validatePrintField(state.initials, 'initials') &&
    validatePrintField(state.number, 'number');
  return valid && !!confirmed;
}

export function prunePrintFromForm(form) {
  const inputs = form.querySelectorAll('[data-klubb-print-input]');
  inputs.forEach((el) => {
    const type = el.getAttribute('data-klubb-print-type');
    const val = (el.value || '').trim();
    if (val === '' || !validatePrintField(val, type)) {
      el.removeAttribute('name');
    }
  });
}

export function readPrintState(scope) {
  const read = (sel) => { const el = scope.querySelector(sel); return el ? el.value : ''; };
  // D43: print_front_numbers is a per-garment flag (no input of its own) surfaced
  // on the personalization wrapper — it gates the extra front-number Trykk line.
  const wrap = scope.querySelector('[data-klubb-personalization]');
  // Name and initials are printed in capitals (the inputs already show them
  // that way), so the summary and the order carry the uppercase value.
  // back: absent attribute = legacy markup where the number was always a back
  // number; the Liquid now sets both flags explicitly.
  const backAttr = wrap ? wrap.getAttribute('data-klubb-print-back') : null;
  return {
    name: read('[data-klubb-print-type="name"]').toUpperCase(),
    initials: read('[data-klubb-print-type="initials"]').toUpperCase(),
    number: read('[data-klubb-print-type="number"]'),
    front: !!(wrap && wrap.getAttribute('data-klubb-print-front') === 'true'),
    back: !!wrap && (backAttr === null ? true : backAttr === 'true'),
  };
}

// Combine the OOS gate and the personalization gate on the add-to-cart
// buttons. Each concern sets a data-* flag; this resolves the final state.
export function updateAddEnabled(scope) {
  const addBtn = scope.querySelector('[data-klubb-add-to-cart]');
  const disabled =
    (addBtn && addBtn.getAttribute('data-klubb-oos') === 'true') ||
    (addBtn && addBtn.getAttribute('data-klubb-print-block') === 'true') || false;
  if (addBtn) addBtn.disabled = disabled;
  const sticky = scope.querySelector('.klubb-product__sticky-cta');
  if (sticky) sticky.disabled = disabled;
}

export function wirePersonalization(scope) {
  const wrap = scope.querySelector('[data-klubb-personalization]');
  if (!wrap) return;
  const inputs = wrap.querySelectorAll('[data-klubb-print-input]');
  const summary = wrap.querySelector('[data-klubb-print-summary]');
  const summaryVal = wrap.querySelector('[data-klubb-print-summary-value]');
  const confirmWrap = wrap.querySelector('[data-klubb-print-confirm-wrap]');
  const confirmCb = wrap.querySelector('[data-klubb-print-confirm]');
  const errorEl = wrap.querySelector('[data-klubb-print-error]');
  const addBtn = scope.querySelector('[data-klubb-add-to-cart]');
  // Print price: shown with the summary («Slik trykkes det: ÆØ · 12  +85,25 kr»)
  // so the cost is visible before the cart. The money format comes from Liquid.
  const priceEl = wrap.querySelector('[data-klubb-print-summary-price]');
  const prices = readTrykkPrices();
  const moneyFormat = wrap.getAttribute('data-klubb-money-format') ||
    (scope.getAttribute && scope.getAttribute('data-klubb-money-format')) || '';

  function refresh() {
    const state = readPrintState(scope);
    const hasValue = printHasValue(state);

    let allValid = true;
    inputs.forEach((el) => {
      const ok = validatePrintField(el.value, el.getAttribute('data-klubb-print-type'));
      el.classList.toggle('is-invalid', !ok);
      if (!ok) allValid = false;
    });
    if (errorEl) errorEl.hidden = allValid;

    if (summary) summary.hidden = !(hasValue && allValid);
    if (summaryVal) summaryVal.textContent = formatPrintSummary(state);
    if (priceEl) {
      const cents = hasValue && allValid ? printPriceCents(state, prices) : 0;
      priceEl.textContent = cents > 0 ? '+' + formatMoney(cents, moneyFormat) : '';
    }
    if (confirmWrap) confirmWrap.hidden = !(hasValue && allValid);
    if (confirmCb && !(hasValue && allValid)) confirmCb.checked = false;

    const complete = isPrintComplete(state, confirmCb && confirmCb.checked);
    if (addBtn) addBtn.setAttribute('data-klubb-print-block', complete ? 'false' : 'true');
    updateAddEnabled(scope);
  }

  inputs.forEach((el) => el.addEventListener('input', refresh));
  if (confirmCb) confirmCb.addEventListener('change', refresh);
  refresh();
}

export function clubCatalogHref(club) {
  const handle = (club && club.catalog_collection_handle) || ((club && club.slug ? club.slug : '') + '-katalog');
  return '/collections/' + handle;
}

/**
 * Breadcrumb middle segment: on a direct /products/ visit the server can't read
 * the klubb_active cookie, so it falls back to the product's collection (which
 * may be another club's catalog). When a club is active client-side and the
 * server didn't already resolve one, replace the segment with the active club's
 * catalog so a BSK member sees "klubb.no / BSK / product".
 */
export function wireBreadcrumbClub(scope) {
  const crumbs = scope.querySelector('[data-klubb-crumbs]');
  if (!crumbs) return;
  if (crumbs.getAttribute('data-klubb-crumbs-club') === 'true') return; // server already did it
  const club = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
  if (!club) return;
  const mid = crumbs.querySelector('[data-klubb-crumb-mid]');
  if (!mid) return;
  mid.textContent = '';
  const sep = document.createElement('span');
  sep.setAttribute('aria-hidden', 'true');
  sep.textContent = '/';
  const a = document.createElement('a');
  a.setAttribute('href', clubCatalogHref(club));
  a.textContent = club.name;
  mid.appendChild(sep);
  mid.appendChild(document.createTextNode(' '));
  mid.appendChild(a);
}

/**
 * D37 — partner-mode: when the active (cookie) club is display-only, mark the
 * PDP so CSS hides the buy controls and shows the "order via club" note. The
 * server already adds is-partner when it resolves a partner club from the URL;
 * this covers direct /products/ visits where the club is cookie-only.
 */
export function wirePartnerMode(scope) {
  const club = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
  if (club && club.mode === 'partner') scope.classList.add('is-partner');
}

export function wireThumbnails(scope) {
  const thumbs = scope.querySelectorAll('[data-klubb-thumb]');
  const mainImg = scope.querySelector('[data-klubb-main-img]');
  if (!thumbs.length || !mainImg) return;
  thumbs.forEach((btn) => {
    btn.addEventListener('click', () => {
      // Prefer the full-res URL — the <img> inside the thumb is a 144px
      // version, and using its src left the main image blurry until refresh.
      const full = btn.dataset.klubbThumbFull;
      const img = btn.querySelector('img');
      const next = full || (img && img.src);
      if (next) mainImg.src = next;
      thumbs.forEach((b) => b.classList.toggle('is-active', b === btn));
    });
  });
}

/** Sticky bar mirrors the chosen variant: photo, «Colour · Size» line and price. */
export function updateStickyBar(scope, variant) {
  if (!variant) return;
  const img = scope.querySelector('[data-klubb-sticky-img]');
  if (img && variant.image) img.src = variant.image;
  const sub = scope.querySelector('[data-klubb-sticky-sub]');
  if (sub) {
    const parts = (variant.options || []).filter((o) => o && o !== 'Default Title');
    if (parts.length) sub.textContent = parts.join(' · ');
  }
  const price = scope.querySelector('[data-klubb-sticky-price]');
  if (price && variant.price) price.textContent = variant.price;
}

/**
 * Gallery follows the colour: show only the thumbs whose file-name stem
 * (data-klubb-media-group, e.g. "231001J_920") matches the variant's image.
 * When no thumb carries the group, the photos do not follow the Umbro naming
 * and every thumb stays visible. Returns the number of visible thumbs.
 */
export function showMediaGroup(scope, group) {
  const thumbs = Array.from(scope.querySelectorAll('[data-klubb-thumb]'));
  if (!thumbs.length) return 0;
  const matching = group ? thumbs.filter((t) => t.dataset.klubbMediaGroup === group) : [];
  const visible = matching.length ? matching : thumbs;
  thumbs.forEach((t) => { t.hidden = visible.indexOf(t) === -1; });
  const mainImg = scope.querySelector('[data-klubb-main-img]');
  const current = mainImg ? mainImg.getAttribute('src') : null;
  const active = visible.find((t) => t.dataset.klubbThumbFull === current) || visible[0];
  thumbs.forEach((t) => t.classList.toggle('is-active', t === active));
  return visible.length;
}

/**
 * Greys out option values that cannot be bought together with the other
 * chosen options (e.g. a size sold out in the chosen colour). The radio stays
 * clickable: the picker then falls back to a variant that exists.
 */
export function markOptionAvailability(scope, variants, selection) {
  scope.querySelectorAll('input[type="radio"][data-klubb-option]').forEach((radio) => {
    const idx = Number(radio.dataset.klubbOption) - 1;
    const probe = selection.slice();
    probe[idx] = radio.value;
    const ok = variants.some((v) => v.available !== false && v.options.every((o, i) => probe[i] == null || o === probe[i]));
    const label = radio.closest('label');
    if (label) label.classList.toggle('is-unavailable', !ok);
  });
}

/**
 * Reflect variant availability in the UI: disable the add-to-cart + sticky
 * CTA for out-of-stock variants (blocking OOS before the checkout wall) and
 * update the stock indicator. Labels come from data-* attributes so the
 * strings stay localized in Liquid.
 */
export function applyAvailability(scope, available) {
  const addBtn = scope.querySelector('[data-klubb-add-to-cart]');
  if (addBtn) {
    addBtn.setAttribute('data-klubb-oos', available ? 'false' : 'true');
    addBtn.classList.toggle('is-oos', !available);
    const label = addBtn.querySelector('[data-klubb-add-label]');
    if (label) {
      const addText = addBtn.getAttribute('data-klubb-label-add');
      const oosText = addBtn.getAttribute('data-klubb-label-oos');
      if (!available && oosText) label.textContent = oosText;
      else if (available && addText) label.textContent = addText;
    }
  }

  updateAddEnabled(scope);

  const stock = scope.querySelector('[data-klubb-stock]');
  if (stock) {
    stock.classList.toggle('is-out', !available);
    const inText = stock.getAttribute('data-klubb-in');
    const outText = stock.getAttribute('data-klubb-out');
    if (available && inText) stock.textContent = inText;
    else if (!available && outText) stock.textContent = outText;
  }
}

export function wireStickyBar(scope) {
  const cta = scope.querySelector('[data-klubb-add-to-cart]');
  const bar = scope.querySelector('[data-klubb-sticky-bar]');
  if (!cta || !bar || typeof IntersectionObserver === 'undefined') return;
  const obs = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      bar.classList.toggle('is-visible', entry.intersectionRatio === 0);
    });
  }, { threshold: 0 });
  obs.observe(cta);
}

// D41: the Trykk (print) product variant map, injected globally by theme.liquid
// from theme settings. { name, initials, number } → variant id or null.
// (Duplicated from klubb-player-package.js — modules don't cross-import.)
export function readTrykkMap(doc) {
  const d = doc || (typeof document !== 'undefined' ? document : null);
  const el = d && d.getElementById('klubb-trykk-data');
  if (!el) return { name: null, initials: null, number: null, front_number: null };
  try {
    const m = JSON.parse(el.textContent);
    return { name: m.name || null, initials: m.initials || null, number: m.number || null, front_number: m.front_number || null };
  } catch (e) {
    return { name: null, initials: null, number: null, front_number: null };
  }
}

const trim = (v) => (v == null ? '' : String(v).trim());
// A state without an explicit back flag is a legacy one: its number is a back number.
const hasBack = (s) => (s.back == null ? true : !!s.back);

// The print prices (cents) from the same map — `prices` was added 18.10.2026 so
// the PDP can say what a print costs before the cart does. Missing → null.
export function readTrykkPrices(doc) {
  const empty = { name: null, initials: null, number: null, front_number: null };
  const d = doc || (typeof document !== 'undefined' ? document : null);
  const el = d && d.getElementById('klubb-trykk-data');
  if (!el) return empty;
  try {
    const p = (JSON.parse(el.textContent) || {}).prices || {};
    const cents = (v) => (typeof v === 'number' && v > 0 ? v : null);
    return { name: cents(p.name), initials: cents(p.initials), number: cents(p.number), front_number: cents(p.front_number) };
  } catch (e) {
    return empty;
  }
}

// What the filled print fields add to the price, in cents: one charge per print
// type the garment gets, the same split as buildPrintItems (back and/or front
// number by the garment's flags). A type without a configured price adds 0.
export function printPriceCents(state, prices) {
  const s = state || {};
  const p = prices || {};
  let total = 0;
  if (trim(s.name) && p.name) total += p.name;
  if (trim(s.initials) && p.initials) total += p.initials;
  const num = trim(s.number);
  if (num && hasBack(s) && p.number) total += p.number;
  if (num && s.front && p.front_number) total += p.front_number;
  return total;
}

// The print properties the garment line carries: name, initials, and the one
// number written to _print_front_number and/or _print_back_number depending on
// the garment's flags. This is the only place the values are written.
export function garmentPrintProps(state) {
  const s = state || {};
  const out = {};
  if (trim(s.name)) out._print_name = trim(s.name);
  if (trim(s.initials)) out._print_initials = trim(s.initials);
  const num = trim(s.number);
  if (num && s.front) out._print_front_number = num;
  if (num && hasBack(s)) out._print_back_number = num;
  return out;
}

// D41: Trykk charge lines for a single garment — one per print type that is
// both filled AND has a configured Trykk product: name, initials, back number
// (print_back_numbers) and front number (print_front_numbers), the last two
// being Traede's generic number products. The lines carry no print values,
// only `_print_for` (the garment variant) and `base` (_print_group,
// _klubb_club) so the cart groups and removes them with the garment.
export function buildPrintItems(state, garmentVariantId, qty, trykk, base) {
  const s = state || {};
  const t = trykk || {};
  const items = [];
  const line = (variantId) => {
    const properties = Object.assign({}, base || {});
    properties._print_for = String(garmentVariantId);
    items.push({ id: variantId, quantity: qty, properties });
  };
  if (trim(s.name) && t.name) line(t.name);
  if (trim(s.initials) && t.initials) line(t.initials);
  const num = trim(s.number);
  if (num && hasBack(s) && t.number) line(t.number);
  if (num && s.front && t.front_number) line(t.front_number);
  return items;
}

export function addToCartMulti(items) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: items }),
  })
    .then((res) => { if (!res.ok) throw new Error('Cart add failed: ' + res.status); return res.json(); })
    .then(() => { window.location.href = '/cart'; })
    .catch((err) => { if (typeof console !== 'undefined' && console.error) console.error('[klubb-product]', err); });
}

export function wireForm(scope) {
  const form = scope.querySelector('[data-klubb-product-form]');
  if (!form) return;
  form.addEventListener('submit', (e) => {
    // The cookie club at the moment of adding, not the one the page loaded
    // with: another tab may have switched (window.klubb re-reads the cookie).
    const nowClub = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
    const propInput = scope.querySelector('[data-klubb-club-prop]');
    if (propInput && nowClub && nowClub.slug) {
      propInput.value = nowClub.slug;
      propInput.name = 'properties[_klubb_club]';
    }
    const state = readPrintState(scope);
    const trykk = readTrykkMap();
    const variantInput = scope.querySelector('[data-klubb-variant-id]');
    const variantId = variantInput ? variantInput.value : '';
    const qtyInput = form.querySelector('[name="quantity"]');
    const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    // Nothing filled in → submit the garment normally, pruning blank/invalid
    // print props so no empty property reaches the order.
    if (!printHasValue(state)) {
      prunePrintFromForm(form);
      return;
    }

    // Print entered → one atomic /cart/add.js with the garment (carrying the
    // values, split into front/back number by the flags) plus the Trykk
    // charge lines sharing a _print_group, then go to the cart. Done in JS
    // even when no Trykk product is configured, so the split never depends
    // on the native form.
    e.preventDefault();
    const club = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
    const clubProp = scope.querySelector('[data-klubb-club-prop]');
    const clubSlug = (club && club.slug) || (clubProp && clubProp.getAttribute('data-klubb-server-slug')) || '';
    const printGroup = 'pg-' + Date.now().toString(36);

    const base = { _print_group: printGroup };
    if (clubSlug) base._klubb_club = clubSlug;
    const printItems = buildPrintItems(state, variantId, qty, trykk, base);

    const garmentProps = Object.assign({ _print_group: printGroup }, garmentPrintProps(state));
    if (clubSlug) garmentProps._klubb_club = clubSlug;
    // D45: carry the club-eligibility token(s) on the garment line (the normal
    // non-print add submits the hidden input directly; this covers the multi-add
    // path). Trykk lines don't need it — they cascade with the garment.
    const eligInput = form.querySelector('[name="properties[_eligible_clubs]"]');
    const eligible = eligInput ? eligInput.value : '';
    if (eligible) garmentProps._eligible_clubs = eligible;
    const garment = { id: Number(variantId), quantity: qty, properties: garmentProps };

    addToCartMulti(cartAddOrder([garment], printItems));
  });
}

/**
 * /cart/add.js prepends each item in turn, so the last item sent ends up on
 * top of the cart, and the checkout lists lines in cart order. Send the
 * Trykk charge lines first and the garment last: the checkout then shows
 * the garment with its print charges right under it, not above it.
 */
export function cartAddOrder(mainItems, printItems) {
  return mainItems.concat(printItems).reverse();
}

/**
 * Write the active club's slug into a hidden line-item property so the
 * selection flows to checkout and downstream order sync (Traede).
 *
 * Server-side Liquid can only resolve the club from the URL (club catalog
 * pages), so on a direct /products/ visit the club is known only client-side
 * via the klubb_active cookie (window.klubb). This fills the property from
 * window.klubb.activeClub, falling back to any server-rendered slug, and
 * removes the property name entirely when no club is active so no blank
 * property reaches the order. The `_` prefix hides it from cart/checkout UI
 * while keeping it on the order line item.
 */
export function wireClubProperty(scope) {
  const input = scope.querySelector('[data-klubb-club-prop]');
  if (!input) return;

  function apply() {
    const club = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
    const slug = (club && club.slug) || input.getAttribute('data-klubb-server-slug') || '';
    if (slug) {
      input.value = slug;
      input.name = 'properties[_klubb_club]';
    } else {
      input.value = '';
      input.removeAttribute('name');
    }
  }

  apply();

  // Re-apply if the visitor selects/changes/clears their club after load.
  if (typeof window !== 'undefined' && window.klubb && typeof window.klubb.on === 'function') {
    window.klubb.on('change', apply);
  }
}

/**
 * Trust line «Klubblisensiert»: Liquid renders the no-club copy on a direct
 * /products/ visit even for a member (the club lives in the klubb_active
 * cookie). Swap the copy from window.klubb.activeClub — same short name the
 * server uses on club-scoped URLs (short_name, else the club name) — and
 * follow club changes.
 */
// The short name of the club a product belongs to, from its eligibility
// tokens: the published club they match (short name, else name), else the
// first token itself in capitals (an unpublished club). '' for general products.
export function productClubShort(scope, doc) {
  const input = scope && scope.querySelector && scope.querySelector('[data-klubb-eligible-clubs]');
  const tokens = parseEligibleTokens(input && input.value);
  if (!tokens.length) return '';
  const d = doc || (typeof document !== 'undefined' ? document : null);
  const el = d && d.getElementById('klubb-clubs-data');
  let clubs = [];
  try { clubs = el ? JSON.parse(el.textContent || '[]') : []; } catch (e) { clubs = []; }
  for (const c of Array.isArray(clubs) ? clubs : []) {
    if (!c) continue;
    const ids = [c.slug, c.short_name, c.crest_initials].filter(Boolean).map((v) => String(v).toLowerCase());
    if (tokens.some((t) => ids.indexOf(t) !== -1)) return c.short_name || c.name || '';
  }
  return tokens[0].toUpperCase();
}

function parseEligibleTokens(raw) {
  return String(raw == null ? '' : raw).toLowerCase().split(/[\s,]+/).map((t) => t.trim()).filter(Boolean);
}

export function wireTrustLine(scope) {
  const el = scope.querySelector('[data-klubb-trust-licensed]');
  if (!el) return;
  const withClub = el.getAttribute('data-klubb-with-club') || '';
  const noClub = el.getAttribute('data-klubb-no-club') || '';
  // A club-specific product carries that club's logo and print whichever
  // club is selected (18.10.2026) — name the product's club, not the member's.
  const productShort = productClubShort(scope);

  function apply() {
    const club = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
    const short = club ? (productShort || club.short_name || club.name || '') : '';
    el.textContent = short && withClub ? withClub.replace('[club_short]', short) : noClub;
  }

  apply();
  if (typeof window !== 'undefined' && window.klubb && typeof window.klubb.on === 'function') {
    window.klubb.on('change', apply);
  }
}

// Shopify's standard money formatter — the PDP variant JSON ships pre-formatted
// regular prices, but the klubbpris is computed in JS (cents × club %), so it
// has to be formatted client-side using the shop's money_format.
export function formatMoney(cents, format) {
  const fmt = format || '${{amount}}';
  let n = typeof cents === 'string' ? cents.replace(/[^\d.-]/g, '') : cents;
  n = Number(n);
  if (isNaN(n)) n = 0;
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  function withDelimiters(amount, precision, thousands, decimal) {
    const value = (amount / 100.0).toFixed(precision);
    const parts = value.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }
  const key = (fmt.match(placeholder) || [null, 'amount'])[1];
  let out;
  switch (key) {
    case 'amount_no_decimals': out = withDelimiters(n, 0, ',', '.'); break;
    case 'amount_with_comma_separator': out = withDelimiters(n, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': out = withDelimiters(n, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': out = withDelimiters(n, 2, "'", '.'); break;
    case 'amount_with_space_separator': out = withDelimiters(n, 2, ' ', ','); break;
    case 'amount_no_decimals_with_space_separator': out = withDelimiters(n, 0, ' ', ''); break;
    case 'amount_with_period_and_space_separator': out = withDelimiters(n, 2, ' ', '.'); break;
    default: out = withDelimiters(n, 2, ',', '.');
  }
  return fmt.replace(placeholder, out);
}

// D35 — apply the club discount to the price block when the active (cookie)
// club has a discount_percent and the product is flagged
// traede.eligible_for_discount. Since 30.09.2026 the product no longer has to
// sit in the club's own catalog: klubbpris covers the whole eligible range.
// Runs on load and on every variant change (via the klubb:variant event).
export function wireKlubbpris(scope) {
  if (scope.dataset.klubbEligibleDiscount !== 'true') return;
  const club = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
  const pct = club ? Number(club.discount_percent) : 0;
  if (!club || !(pct > 0)) return;

  const nowEl = scope.querySelector('[data-klubb-price-now]');
  const regEl = scope.querySelector('[data-klubb-price-regular]');
  const labelEl = scope.querySelector('[data-klubb-price-label]');
  if (!nowEl) return;
  const moneyFormat = scope.dataset.klubbMoneyFormat || '${{amount}}';
  const label = (club.short_name || club.name || '') + '-pris';

  const variantsScript = scope.querySelector('[data-klubb-variants]');
  let variants = [];
  try { variants = JSON.parse(variantsScript.textContent); } catch (e) { variants = []; }

  function apply(variant) {
    if (!variant || variant.priceCents == null) return;
    const clubCents = Math.round((variant.priceCents * (100 - pct)) / 100);
    nowEl.textContent = formatMoney(clubCents, moneyFormat);
    const stickyPrice = scope.querySelector('[data-klubb-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = formatMoney(clubCents, moneyFormat);
    if (regEl) { regEl.textContent = variant.price || formatMoney(variant.priceCents, moneyFormat); regEl.hidden = false; }
    if (labelEl) { labelEl.textContent = label; labelEl.hidden = false; }
  }

  // Initial variant — match the form's current variant id, else the first.
  const idInput = scope.querySelector('[data-klubb-variant-id]');
  const currentId = idInput ? Number(idInput.value) : null;
  apply(variants.find((v) => v.id === currentId) || variants[0]);

  scope.addEventListener('klubb:variant', (e) => apply(e.detail && e.detail.variant));
}

export function findMatchingVariant(variants, selection) {
  return variants.find((v) =>
    v.options.every((opt, i) => opt === selection[i])
  );
}

export function wireVariantPicker(scope) {
  const variantsScript = scope.querySelector('[data-klubb-variants]');
  const idInput = scope.querySelector('[data-klubb-variant-id]');
  const priceEl = scope.querySelector('[data-klubb-price-now]');
  const mainImg = scope.querySelector('[data-klubb-main-img]');
  const radios = scope.querySelectorAll('input[type="radio"][data-klubb-option]');
  if (!variantsScript || !idInput || !radios.length) return;

  let variants;
  try {
    variants = JSON.parse(variantsScript.textContent);
  } catch (e) {
    return;
  }

  // Capture the product's default image src once so we can fall back
  // to it when a picked variant has no variant-specific image.
  const defaultImgSrc = mainImg ? mainImg.src : null;
  const optionCount = Math.max(...Array.from(radios, (r) => Number(r.dataset.klubbOption)));

  function readSelection() {
    const sel = new Array(optionCount).fill(null);
    radios.forEach((r) => {
      if (r.checked) sel[Number(r.dataset.klubbOption) - 1] = r.value;
    });
    return sel;
  }

  function syncRadiosToVariant(variant) {
    variant.options.forEach((optVal, i) => {
      const radio = scope.querySelector(
        `input[type="radio"][data-klubb-option="${i + 1}"][value="${optVal}"]`
      );
      if (radio && !radio.checked) radio.checked = true;
    });
  }

  function sync(changedOptionIdx) {
    const selection = readSelection();
    let variant = findMatchingVariant(variants, selection);

    // Sparse variant matrix: the just-changed option may not combine
    // with the others' current values. Fall back to first variant
    // whose changed-option value matches (prefer available), then
    // auto-update the other radios.
    if (!variant && changedOptionIdx != null) {
      const optIdx = changedOptionIdx - 1;
      const newValue = selection[optIdx];
      variant = variants.find((v) => v.options[optIdx] === newValue && v.available)
             || variants.find((v) => v.options[optIdx] === newValue);
      if (variant) syncRadiosToVariant(variant);
    }

    if (!variant) return;
    idInput.value = String(variant.id);
    if (priceEl) priceEl.textContent = variant.price;
    if (mainImg) mainImg.src = variant.image || defaultImgSrc || mainImg.src;
    showMediaGroup(scope, variant.imageGroup);
    updateStickyBar(scope, variant);
    variant.options.forEach((optVal, i) => {
      const valueEl = scope.querySelector(`[data-klubb-option-value="${i + 1}"]`);
      if (valueEl) valueEl.textContent = optVal;
    });
    markOptionAvailability(scope, variants, variant.options);
    applyAvailability(scope, variant.available !== false);
    // Let klubbpris (D35) recompute the discounted price for the new variant.
    scope.dispatchEvent(new CustomEvent('klubb:variant', { detail: { variant } }));
  }

  radios.forEach((r) => {
    r.addEventListener('change', () => sync(Number(r.dataset.klubbOption)));
  });

  // Initial state: gallery and size availability for the preselected variant.
  const initial = variants.find((v) => v.id === Number(idInput.value)) || findMatchingVariant(variants, readSelection());
  if (initial) {
    // The server renders the product's featured image, which may belong to
    // another colour than the preselected variant.
    if (mainImg && initial.image) mainImg.src = initial.image;
    showMediaGroup(scope, initial.imageGroup);
    updateStickyBar(scope, initial);
    markOptionAvailability(scope, variants, initial.options);
  }
}

function init() {
  document.querySelectorAll('[data-klubb-product]').forEach((scope) => {
    wirePersonalization(scope);
    wireBreadcrumbClub(scope);
    wirePartnerMode(scope);
    wireThumbnails(scope);
    wireStickyBar(scope);
    wireForm(scope);
    wireVariantPicker(scope);
    wireKlubbpris(scope);
    wireClubProperty(scope);
    wireTrustLine(scope);
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
